/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {DEFAULT_BEHAVIOR} from '../../constants/defaultBehavior';
import {BehaviorResponseBody, PresetDisplayMode} from '../../types/Api';
import type {EagGridState} from '../createEagGridStore';
import {useEagGridSelector} from '../useEagGridSelector';

/**
 * Module-level selector functions for behavior flags.
 *
 * Each selector returns a primitive value (boolean, string, enum) so
 * useSyncExternalStore can compare with Object.is() — stable references
 * when the underlying definition.behavior hasn't changed.
 *
 * All selectors fall back to DEFAULT_BEHAVIOR when definition is not loaded.
 */

const selectBehavior = (state: EagGridState): BehaviorResponseBody =>
  state.definition?.behavior ?? DEFAULT_BEHAVIOR;

export const selectRowSelectMode = (state: EagGridState): 'SINGLE' | 'MULTIPLE' | 'NONE' =>
  selectBehavior(state).rowSelectMode;

export const selectWrapperStyle = (state: EagGridState): 'CARD' | 'INLINE' =>
  selectBehavior(state).wrapperStyle;

export const selectExportAllowed = (state: EagGridState): boolean =>
  selectBehavior(state).exportAllowed !== false;

/**
 * Resolves the preset display mode.
 *
 * Priority: grid-scoped local preference →
 * definition `behavior.presetDisplayMode` default → legacy `presetsEnabled` boolean.
 */
export const selectPresetDisplayMode = (state: EagGridState): PresetDisplayMode => {
  const behavior = selectBehavior(state);
  return (
    state.presetDisplayModeOverride ??
    behavior.presetDisplayMode ??
    (behavior.presetsEnabled ? 'DROPDOWN' : 'NONE')
  );
};

/**
 * Hook wrappers for component use.
 *
 * useBehaviorFlag<K> builds a per-call selector that reads behavior[K] off the
 * selectBehavior result (a stable object reference while `definition` is
 * unchanged — not a memo). Each component still subscribes to only its own flag:
 * the returned primitive is compared with Object.is by useSyncExternalStore, so
 * a change to one flag does not re-render components reading a different flag.
 */
export function useBehaviorFlag<K extends keyof BehaviorResponseBody>(
  key: K
): BehaviorResponseBody[K] {
  return useEagGridSelector((state) => selectBehavior(state)[key]);
}

export const useSmartSearchEnabled = (): boolean => useBehaviorFlag('smartSearchEnabled');
export const usePresetsEnabled = (): boolean => useBehaviorFlag('presetsEnabled');

/** Returns the resolved preset display mode. See {@link selectPresetDisplayMode}. */
export const usePresetDisplayMode = (): PresetDisplayMode =>
  useEagGridSelector(selectPresetDisplayMode);
export const useGlobalActionsEnabled = (): boolean => useBehaviorFlag('globalActionsEnabled');
export const useFilterToolPanelVisible = (): boolean => useBehaviorFlag('filterToolPanelVisible');
export const useColumnToolPanelVisible = (): boolean => useBehaviorFlag('columnToolPanelVisible');
export const useSettingsToolPanelVisible = (): boolean =>
  useBehaviorFlag('settingsToolPanelVisible');
export const useColumnMovingAllowed = (): boolean => useBehaviorFlag('columnMovingAllowed');
export const useColumnResizingAllowed = (): boolean => useBehaviorFlag('columnResizingAllowed');
export const useColumnSortMode = (): 'SINGLE_SORT' | 'MULTI_SORT' | 'NONE' =>
  useBehaviorFlag('columnSortMode');
export const useColumnMenuEnabled = (): boolean => useBehaviorFlag('columnMenuEnabled');
export const useColumnPinningAllowed = (): boolean => useBehaviorFlag('columnPinningAllowed');
export const useColumnAutoSizingAllowed = (): boolean => useBehaviorFlag('columnAutoSizingAllowed');
export const useColumnHidingAllowed = (): boolean => useBehaviorFlag('columnHidingAllowed');
export const useActionColumnEnabled = (): boolean => useBehaviorFlag('actionColumnEnabled');
export const usePaginationEnabled = (): boolean => useBehaviorFlag('paginationEnabled');
export const useClipboardExportAllowed = (): boolean => useBehaviorFlag('clipboardExportAllowed');
export const useExportAllowed = (): boolean => useEagGridSelector(selectExportAllowed);
export const useContextMenuEnabled = (): boolean => useBehaviorFlag('contextMenuEnabled');
export const useSummaryRowEnabled = (): boolean => useBehaviorFlag('summaryRowEnabled');
export const useStatusBarVisible = (): boolean => useBehaviorFlag('statusBarVisible');
export const useRangeSelectAllowed = (): boolean => useBehaviorFlag('rangeSelectAllowed');
export const useWrapperStyle = (): 'CARD' | 'INLINE' => useEagGridSelector(selectWrapperStyle);

export const useSortedColumnCount = (): number =>
  useEagGridSelector((state) => state.columnStates.filter((cs) => cs.sort != null).length);
